export interface PiecePosition {
  x: number;
  y: number;
}

export class Piece {
  public currentX: number;
  public currentY: number;
  public correctX: number;
  public correctY: number;
  public width: number;
  public height: number;
  public row: number;
  public col: number;
  public isPlaced: boolean = false;
  public isSelected: boolean = false;
  public gridRow: number;
  public gridCol: number;

  constructor(
    row: number,
    col: number,
    correctX: number,
    correctY: number,
    width: number,
    height: number
  ) {
    this.row = row;
    this.col = col;
    this.correctX = correctX;
    this.correctY = correctY;
    this.currentX = correctX;
    this.currentY = correctY;
    this.width = width;
    this.height = height;
    this.gridRow = row;
    this.gridCol = col;
  }

  containsPoint(x: number, y: number): boolean {
    return (
      x >= this.currentX &&
      x <= this.currentX + this.width &&
      y >= this.currentY &&
      y <= this.currentY + this.height
    );
  }

  moveTo(x: number, y: number): void {
    this.currentX = x;
    this.currentY = y;
  }

  setGridPosition(gridRow: number, gridCol: number, gridX: number, gridY: number): void {
    this.gridRow = gridRow;
    this.gridCol = gridCol;
    this.currentX = gridX;
    this.currentY = gridY;
  }

  isInCorrectPosition(): boolean {
    return this.gridRow === this.row && this.gridCol === this.col;
  }

  draw(
    ctx: CanvasRenderingContext2D,
    image: HTMLImageElement,
    sourceWidth: number,
    sourceHeight: number,
    neighbors?: { top?: Piece; right?: Piece; bottom?: Piece; left?: Piece }
  ): void {
    const sourceX = this.col * sourceWidth;
    const sourceY = this.row * sourceHeight;

    // Draw with slight overlap to prevent gaps between connected pieces
    const overlap = 0.5;
    ctx.drawImage(
      image,
      sourceX,
      sourceY,
      sourceWidth,
      sourceHeight,
      this.currentX - overlap,
      this.currentY - overlap,
      this.width + overlap * 2,
      this.height + overlap * 2
    );

    // Draw borders only where tiles are NOT adjacent in the final image
    if (neighbors) {
      // Top border - draw if the piece above is not our correct top neighbor
      const correctTopRow = this.row - 1;
      const correctTopCol = this.col;
      if (!neighbors.top || neighbors.top.row !== correctTopRow || neighbors.top.col !== correctTopCol) {
        // Black outer border
        ctx.strokeStyle = 'rgba(0, 0, 0, 1.0)';
        ctx.lineWidth = this.isSelected ? 4 : 3;
        ctx.beginPath();
        ctx.moveTo(this.currentX, this.currentY);
        ctx.lineTo(this.currentX + this.width, this.currentY);
        ctx.stroke();

        // White/Gold inner border
        ctx.strokeStyle = this.isSelected ? '#FFD700' : 'rgba(255, 255, 255, 1.0)';
        ctx.lineWidth = this.isSelected ? 2 : 1;
        ctx.beginPath();
        ctx.moveTo(this.currentX, this.currentY);
        ctx.lineTo(this.currentX + this.width, this.currentY);
        ctx.stroke();
      }

      // Right border - draw if the piece to the right is not our correct right neighbor
      const correctRightRow = this.row;
      const correctRightCol = this.col + 1;
      if (!neighbors.right || neighbors.right.row !== correctRightRow || neighbors.right.col !== correctRightCol) {
        // Black outer border
        ctx.strokeStyle = 'rgba(0, 0, 0, 1.0)';
        ctx.lineWidth = this.isSelected ? 4 : 3;
        ctx.beginPath();
        ctx.moveTo(this.currentX + this.width, this.currentY);
        ctx.lineTo(this.currentX + this.width, this.currentY + this.height);
        ctx.stroke();

        // White/Gold inner border
        ctx.strokeStyle = this.isSelected ? '#FFD700' : 'rgba(255, 255, 255, 1.0)';
        ctx.lineWidth = this.isSelected ? 2 : 1;
        ctx.beginPath();
        ctx.moveTo(this.currentX + this.width, this.currentY);
        ctx.lineTo(this.currentX + this.width, this.currentY + this.height);
        ctx.stroke();
      }

      // Bottom border - draw if the piece below is not our correct bottom neighbor
      const correctBottomRow = this.row + 1;
      const correctBottomCol = this.col;
      if (!neighbors.bottom || neighbors.bottom.row !== correctBottomRow || neighbors.bottom.col !== correctBottomCol) {
        // Black outer border
        ctx.strokeStyle = 'rgba(0, 0, 0, 1.0)';
        ctx.lineWidth = this.isSelected ? 4 : 3;
        ctx.beginPath();
        ctx.moveTo(this.currentX, this.currentY + this.height);
        ctx.lineTo(this.currentX + this.width, this.currentY + this.height);
        ctx.stroke();

        // White/Gold inner border
        ctx.strokeStyle = this.isSelected ? '#FFD700' : 'rgba(255, 255, 255, 1.0)';
        ctx.lineWidth = this.isSelected ? 2 : 1;
        ctx.beginPath();
        ctx.moveTo(this.currentX, this.currentY + this.height);
        ctx.lineTo(this.currentX + this.width, this.currentY + this.height);
        ctx.stroke();
      }

      // Left border - draw if the piece to the left is not our correct left neighbor
      const correctLeftRow = this.row;
      const correctLeftCol = this.col - 1;
      if (!neighbors.left || neighbors.left.row !== correctLeftRow || neighbors.left.col !== correctLeftCol) {
        // Black outer border
        ctx.strokeStyle = 'rgba(0, 0, 0, 1.0)';
        ctx.lineWidth = this.isSelected ? 4 : 3;
        ctx.beginPath();
        ctx.moveTo(this.currentX, this.currentY);
        ctx.lineTo(this.currentX, this.currentY + this.height);
        ctx.stroke();

        // White/Gold inner border
        ctx.strokeStyle = this.isSelected ? '#FFD700' : 'rgba(255, 255, 255, 1.0)';
        ctx.lineWidth = this.isSelected ? 2 : 1;
        ctx.beginPath();
        ctx.moveTo(this.currentX, this.currentY);
        ctx.lineTo(this.currentX, this.currentY + this.height);
        ctx.stroke();
      }
    } else {
      // Fallback to drawing full rectangle if no neighbor info provided
      // Black outer border
      ctx.strokeStyle = 'rgba(0, 0, 0, 1.0)';
      ctx.lineWidth = this.isSelected ? 4 : 3;
      ctx.strokeRect(this.currentX, this.currentY, this.width, this.height);

      // White/Gold inner border
      ctx.strokeStyle = this.isSelected ? '#FFD700' : 'rgba(255, 255, 255, 1.0)';
      ctx.lineWidth = this.isSelected ? 2 : 1;
      ctx.strokeRect(this.currentX, this.currentY, this.width, this.height);
    }
  }
}
